// 강의실 유형 — 센터설정에서 지정하고, 레슨 등록 화면의 강의실 목록을 거르는 데 쓴다.
// 값은 백엔드 BranchExerciseRoomType 과 1:1로 맞춘다.
export const ROOM_TYPE = {
  ALL: 'ALL',
  PRIVATE_LESSON: 'PRIVATE_LESSON',
  GROUP_LESSON: 'GROUP_LESSON',
  CONSULTATION: 'CONSULTATION',
};

export const ROOM_TYPE_OPTIONS = [
  { value: ROOM_TYPE.ALL, label: '전체사용가능 룸' },
  { value: ROOM_TYPE.PRIVATE_LESSON, label: '개인레슨실(듀엣포함)' },
  { value: ROOM_TYPE.GROUP_LESSON, label: '그룹레슨실' },
  { value: ROOM_TYPE.CONSULTATION, label: '상담전용룸' },
];

export const ROOM_TYPE_LABELS = ROOM_TYPE_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.value]: option.label }),
  {},
);

// 레슨구분별로 고를 수 있는 강의실 유형. 여기 없는 레슨구분(온라인레슨 등)은 필터하지 않는다.
const ALLOWED_ROOM_TYPES = {
  PRIVATE_LESSON: [ROOM_TYPE.PRIVATE_LESSON, ROOM_TYPE.ALL],
  DUET_LESSON: [ROOM_TYPE.PRIVATE_LESSON, ROOM_TYPE.ALL],
  // OT 는 정원이 여러 명일 수 있어 개인·그룹 강의실 모두 허용
  OT: [ROOM_TYPE.PRIVATE_LESSON, ROOM_TYPE.GROUP_LESSON, ROOM_TYPE.ALL],
  GROUP_LESSON: [ROOM_TYPE.GROUP_LESSON, ROOM_TYPE.ALL],
  COUNSEL: [ROOM_TYPE.CONSULTATION, ROOM_TYPE.ALL],
  CONSULTATION: [ROOM_TYPE.CONSULTATION, ROOM_TYPE.ALL],
};

// 강의실 목록은 화면마다 키가 달라서(branchExerciseRoomId / roomId) 여기서 흡수한다.
export const getRoomId = room => room?.branchExerciseRoomId ?? room?.roomId ?? null;

// 이 강의실을 해당 레슨구분에서 고를 수 있는지. 매핑에 없는 레슨구분은 제한하지 않는다.
export const isRoomAllowedForLesson = (room, lessonType) => {
  const allowed = ALLOWED_ROOM_TYPES[lessonType];
  if (!allowed) return true;
  return allowed.includes(room?.roomType || ROOM_TYPE.ALL);
};

/**
 * 레슨구분에 맞는 강의실만 남긴다.
 * 유형이 비어 있는 강의실은 전체사용가능으로 본다(기존 데이터 호환).
 * 이미 선택돼 있는 강의실(keepRoomId)은 유형이 안 맞아도 남긴다 — 수정 시 값이 사라지는 걸 막는다.
 */
export const filterRoomsByLessonType = (rooms, lessonType, keepRoomId = null) => {
  const allowed = ALLOWED_ROOM_TYPES[lessonType];
  if (!Array.isArray(rooms) || !allowed) return rooms || [];
  return rooms.filter(room => {
    const roomId = getRoomId(room);
    if (keepRoomId != null && roomId === keepRoomId) return true;
    return allowed.includes(room?.roomType || ROOM_TYPE.ALL);
  });
};

// 레슨구분 → 기본 강의실 플래그 필드. 개인·듀엣은 같은 기본을 쓴다.
export const DEFAULT_FLAG_FIELD = {
  PRIVATE_LESSON: 'defaultForPrivateYn',
  DUET_LESSON: 'defaultForPrivateYn',
  GROUP_LESSON: 'defaultForGroupYn',
  COUNSEL: 'defaultForConsultationYn',
  CONSULTATION: 'defaultForConsultationYn',
};

// 강의실 유형별로 기본 지정이 가능한 레슨구분. 전체사용가능은 셋 다 지정할 수 있다.
export const DEFAULT_TARGETS_BY_ROOM_TYPE = {
  [ROOM_TYPE.ALL]: ['PRIVATE_LESSON', 'GROUP_LESSON', 'CONSULTATION'],
  [ROOM_TYPE.PRIVATE_LESSON]: ['PRIVATE_LESSON'],
  [ROOM_TYPE.GROUP_LESSON]: ['GROUP_LESSON'],
  [ROOM_TYPE.CONSULTATION]: ['CONSULTATION'],
};

export const DEFAULT_TARGET_LABELS = {
  PRIVATE_LESSON: '개인·듀엣레슨',
  GROUP_LESSON: '그룹레슨',
  CONSULTATION: '상담',
};

// 카드 뱃지에 쓰는 짧은 라벨
export const DEFAULT_TARGET_SHORT_LABELS = {
  PRIVATE_LESSON: '개인 기본',
  GROUP_LESSON: '그룹 기본',
  CONSULTATION: '상담 기본',
};

/**
 * 자동으로 채울 강의실 id.
 * 해당 레슨구분의 기본 강의실이 있으면 그곳을, 없으면 고를 수 있는 곳이 하나뿐일 때 그곳을 돌려준다.
 */
export const pickAutoRoomId = (rooms, lessonType) => {
  if (!rooms?.length) return null;
  const field = DEFAULT_FLAG_FIELD[lessonType];
  const defaultRoom = field ? rooms.find(room => room?.[field] === 'Y') : null;
  if (defaultRoom) return getRoomId(defaultRoom);
  return rooms.length === 1 ? getRoomId(rooms[0]) : null;
};
